import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { ChevronDown, Check, X } from "lucide-react";



export default function CustomSelect({
  value,
  onChange,
  name,
  id,
  options = [],
  children,
  placeholder = "Chọn...",
  className = "",
  buttonClassName = "",
  disabled = false,
  error = false,
  hasError = false,
  searchable,
}) {
  const [open, setOpen] = useState(false);
  const [dropUp, setDropUp] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const [focusedIdx, setFocusedIdx] = useState(-1);

  const isError = Boolean(error || hasError);

  // Parse options: Ưu tiên options array, nếu không có thì đọc từ thẻ con <option>
  const parsedOptions = useMemo(() => {
    if (Array.isArray(options) && options.length > 0) {
      return options.map((opt) =>
        typeof opt === "object" && opt !== null
          ? {
              ...opt,
              value: opt.value ?? "",
              label: opt.label !== undefined ? String(opt.label) : String(opt.value ?? ""),
              disabled: Boolean(opt.disabled),
            }
          : { value: opt, label: String(opt), disabled: false }
      );
    }
    if (!children) return [];
    const opts = [];
    React.Children.forEach(children, (child) => {
      if (!child) return;
      if (child.props) {
        opts.push({
          value: child.props.value ?? "",
          label: child.props.children !== undefined ? String(child.props.children) : String(child.props.value ?? ""),
          disabled: Boolean(child.props.disabled),
        });
      }
    });
    return opts;
  }, [options, children]);

  // Tìm option đang được chọn
  const selectedOption = useMemo(() => {
    return parsedOptions.find(
      (opt) => opt.value === value || String(opt.value) === String(value ?? "")
    );
  }, [parsedOptions, value]);


  // Quyết định có cho phép gõ tìm kiếm không (mặc định tự động khi >= 7 options hoặc truyền prop searchable)
  const isSearchable = searchable !== undefined ? searchable : parsedOptions.length >= 7;

  // Lọc options theo từ khóa tìm kiếm
  const filteredOptions = useMemo(() => {
    if (!isSearchable || !searchTerm.trim()) return parsedOptions;
    const lower = searchTerm.toLowerCase().trim();
    return parsedOptions.filter((opt) =>
      String(opt.label || "").toLowerCase().includes(lower)
    );
  }, [parsedOptions, isSearchable, searchTerm]);

  // Kiểm tra hướng mở popup (mở lên trên nếu gần đáy màn hình và đủ chỗ phía trên)
  useEffect(() => {
    if (open && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      // Ước tính chiều cao popup dựa trên số lượng options (tối đa 230px tương đương max-h-56)
      const optionsCount = filteredOptions.length || parsedOptions.length || 1;
      const estimatedHeight = Math.min(optionsCount * 38 + 16, 230);

      // Tìm scroll parent gần nhất nếu có (để tránh dropUp khi ở gần đỉnh scroll container dẫn đến bị cắt khuất)
      let isNearScrollParentTop = false;
      let el = containerRef.current.parentElement;
      while (el && el !== document.body && el !== document.documentElement) {
        const style = window.getComputedStyle(el);
        const overflowY = style.overflowY || style.overflow;
        if (overflowY === "auto" || overflowY === "scroll" || overflowY === "hidden") {
          const parentRect = el.getBoundingClientRect();
          const spaceAboveInParent = rect.top - parentRect.top;
          if (spaceAboveInParent < estimatedHeight) {
            isNearScrollParentTop = true;
          }
          break;
        }
        el = el.parentElement;
      }

      // Chỉ mở lên trên (dropUp) khi:
      // 1. Không bị cấn đỉnh scroll parent (nếu có scroll parent)
      // 2. Không đủ chỗ bên dưới cho estimatedHeight
      // 3. Phía trên có nhiều chỗ hơn phía dưới VÀ đủ chỗ cho estimatedHeight
      const shouldDropUp =
        !isNearScrollParentTop &&
        spaceBelow < estimatedHeight &&
        spaceAbove >= estimatedHeight &&
        spaceAbove > spaceBelow;

      setDropUp(shouldDropUp);
    } else {
      setSearchTerm("");
      setFocusedIdx(-1);
    }
  }, [open, filteredOptions.length, parsedOptions.length]);

  // Đóng dropdown khi click ra ngoài
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Cuộn item được focus vào tầm nhìn
  useEffect(() => {
    if (open && focusedIdx >= 0 && listRef.current) {
      const item = listRef.current.children[focusedIdx];
      if (item) {
        item.scrollIntoView({ block: "nearest" });
      }
    }
  }, [focusedIdx, open]);

  const handleSelect = useCallback(
    (opt) => {
      if (opt.disabled) return;
      if (onChange) {
        if (name) {
          onChange({
            target: { name, value: opt.value },
            currentTarget: { name, value: opt.value },
            value: opt.value,
          });
        } else {
          onChange(opt.value);
        }
      }
      setOpen(false);
      setSearchTerm("");
    },
    [onChange, name]
  );

  const handleKeyDown = (e) => {
    if (disabled) return;
    if (!open) {
      if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
        e.preventDefault();
        setOpen(true);
        setFocusedIdx(0);
        setTimeout(() => inputRef.current?.focus(), 10);
      }
      return;
    }

    switch (e.key) {
      case "Escape":
        setOpen(false);
        setSearchTerm("");
        break;
      case "ArrowDown":
        e.preventDefault();
        setFocusedIdx((prev) => (prev < filteredOptions.length - 1 ? prev + 1 : 0));
        break;
      case "ArrowUp":
        e.preventDefault();
        setFocusedIdx((prev) => (prev > 0 ? prev - 1 : filteredOptions.length - 1));
        break;
      case "Enter":
        e.preventDefault();
        if (focusedIdx >= 0 && focusedIdx < filteredOptions.length) {
          handleSelect(filteredOptions[focusedIdx]);
        }
        break;
      case "Tab":
        setOpen(false);
        setSearchTerm("");
        break;
      default:
        break;
    }
  };

  const handleTriggerClick = (e) => {
    if (disabled) return;
    if (!open) {
      setOpen(true);
      setTimeout(() => inputRef.current?.focus(), 10);
    } else {
      if (e.target !== inputRef.current) {
        setOpen(false);
      }
    }
  };

  return (
    <div
      ref={containerRef}
      className={`relative ${open ? "z-40" : ""} ${className}`}
      onKeyDown={handleKeyDown}
      id={id}
    >
      {/* Trigger Box chứa ô tìm kiếm trực tiếp */}
      <div
        onClick={handleTriggerClick}
        className={`flex items-center justify-between gap-2 w-full min-h-[40px] px-3.5 py-2 border rounded-lg text-sm transition-all duration-150 cursor-pointer
          ${disabled ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed shadow-none" : "bg-white"}
          ${
            isError
              ? "border-red-500 ring-1 ring-red-500/20 text-red-900"
              : open
              ? "border-blue-500 ring-2 ring-blue-500/20 text-gray-900 shadow-xs"
              : "border-gray-300 text-gray-800 hover:border-gray-400 hover:bg-gray-50/40"
          }
          ${buttonClassName}
        `}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <input
            ref={inputRef}
            type="text"
            disabled={disabled}
            readOnly={!isSearchable}
            value={open ? searchTerm : (selectedOption ? selectedOption.label : "")}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              if (!open) setOpen(true);
              setFocusedIdx(0);
            }}
            onFocus={() => {
              if (!disabled) {
                setOpen(true);
              }
            }}
            placeholder={
              open && selectedOption
                ? selectedOption.label
                : placeholder
            }
            className={`w-full bg-transparent border-none outline-none text-sm p-0 focus:ring-0 focus:outline-none min-w-0
              ${disabled ? "cursor-not-allowed text-gray-400" : isSearchable ? "cursor-text" : "cursor-pointer"}
              ${open && selectedOption && !searchTerm ? "placeholder:text-gray-600 font-normal" : "placeholder:text-gray-400"}
              ${!open && selectedOption && selectedOption.value !== "" ? "text-gray-900 font-normal" : ""}
            `}
            autoComplete="off"
          />
        </div>

        <div className="flex items-center gap-1 flex-shrink-0">
          {open && searchTerm && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setSearchTerm("");
                inputRef.current?.focus();
              }}
              className="p-0.5 text-gray-400 hover:text-gray-600 rounded cursor-pointer transition-colors"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
          <ChevronDown
            className={`w-4 h-4 text-gray-400 transition-transform duration-200 pointer-events-none ${
              open ? "rotate-180 text-blue-600" : ""
            }`}
          />
        </div>
      </div>

      {/* Dropdown Menu (Chỉ hiển thị danh sách, không còn ô tìm kiếm thừa bên trong) */}
      {open && (
        <div
          className={`absolute z-50 left-0 right-0 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden animate-in fade-in zoom-in-95 duration-100
            ${dropUp ? "bottom-full mb-1.5 shadow-xl" : "top-full mt-1.5 shadow-xl"}
          `}
        >
          {/* Danh sách options */}
          <div ref={listRef} className="py-1 max-h-56 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <div className="p-3 text-center text-xs text-gray-400 italic">
                Không tìm thấy kết quả
              </div>
            ) : (
              filteredOptions.map((opt, idx) => {
                const isSelected =
                  opt.value === value || String(opt.value) === String(value ?? "");
                const isFocused = idx === focusedIdx;

                return (
                  <button
                    key={opt.value !== "" ? opt.value : `empty-${idx}`}
                    type="button"
                    disabled={opt.disabled}
                    onClick={() => handleSelect(opt)}
                    onMouseEnter={() => setFocusedIdx(idx)}
                    className={`flex items-center justify-between gap-2 w-full px-3 py-2 text-xs sm:text-sm text-left transition-colors duration-100 cursor-pointer rounded-lg mx-1 my-0.5
                      ${opt.disabled ? "opacity-40 cursor-not-allowed bg-transparent" : ""}
                      ${
                        isSelected
                          ? "bg-blue-50 text-blue-700 font-medium"
                          : isFocused
                          ? "bg-slate-100 text-gray-900"
                          : "text-gray-700 hover:bg-slate-50"
                      }
                    `}
                    style={{ width: "calc(100% - 8px)" }}
                  >
                    <span className="truncate flex-1">{opt.label}</span>
                    {isSelected && (
                      <Check className="w-4 h-4 text-blue-600 flex-shrink-0 ml-1.5" />
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
